import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { Column, CellProps } from 'react-table';
import { useTranslation } from 'react-i18next';

import FetchTable from 'components/table/FetchTable';
import TooltipWrapper from 'components/TooltipWrapper';

import { formatDate } from 'utils/helpers/formatters';

interface IProps {
  reportParams: any;
}

interface IReportRow {
  [key: string]: any;
}

const getValue = (obj: any, paths: string[]): any => {
  const values = paths.map(path =>
    path.split('.').reduce((acc: any, curr: string) => (acc ? acc[curr] : undefined), obj)
  );

  const firstFilledValue = values.find(value => value !== undefined && value !== null && value !== '');

  return firstFilledValue ?? '-';
};

const toShortYearDate = (value: any): any => {
  if (typeof value !== 'string') return value;

  const ddmmyyyy = value.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
  const date = ddmmyyyy ? new Date(Number(ddmmyyyy[3]), Number(ddmmyyyy[2]) - 1, Number(ddmmyyyy[1])) : new Date(value);

  if (Number.isNaN(date.getTime())) return value;

  return formatDate(date, 'dd.mm.yy');
};

const formatPeriod = (row: any) => {
  const fromDate = toShortYearDate(getValue(row, ['from_date', 'date_from']));
  const toDate = toShortYearDate(getValue(row, ['to_date', 'date_to']));

  if (fromDate === '-' && toDate === '-') return '-';

  return `${fromDate} - ${toDate}`;
};

const GroupsCell: FC<{ text: string; rowIndex: number }> = ({ text, rowIndex }) => {
  const textRef = useRef<HTMLDivElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const el = textRef.current;
    if (!el) return;

    setIsTruncated(el.scrollWidth > el.clientWidth);
  }, [text]);

  return (
    <TooltipWrapper
      id={`tooltip-work-trips-groups-${rowIndex}`}
      overlay={text}
      show={isTruncated ? undefined : false}
    >
      <div
        ref={textRef}
        style={{
          maxWidth: '240px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {text}
      </div>
    </TooltipWrapper>
  );
};

const TripsTable: FC<IProps> = ({ reportParams }) => {
  const { t } = useTranslation();

  const columns: Array<Column<IReportRow>> = useMemo(
    () => [
      {
        Header: t('workTrips.user'),
        accessor: 'employee_name',
        disableSortBy: true,
        Cell: ({ row }: CellProps<IReportRow>) => <>{getValue(row.original, ['employee_name'])}</>,
      },
      {
        Header: t('workTrips.group'),
        accessor: 'groups',
        disableSortBy: true,
        Cell: ({ row }: CellProps<IReportRow>) => {
          const groups = getValue(row.original, ['groups']);
          const groupsText = Array.isArray(groups)
            ? groups
                .map((g: any) => g?.name || g)
                .filter(Boolean)
                .join(', ') || '-'
            : String(groups ?? '-');

          return (
            <GroupsCell
              text={groupsText}
              rowIndex={row.index}
            />
          );
        },
      },
      {
        Header: t('workTrips.destination'),
        accessor: 'destination',
        disableSortBy: true,
        Cell: ({ row }: CellProps<IReportRow>) => (
          <>{getValue(row.original, ['displayed_destination', 'destination'])}</>
        ),
      },
      {
        Header: t('workTrips.route'),
        accessor: 'route',
        disableSortBy: true,
        Cell: ({ row }: CellProps<IReportRow>) => <>{getValue(row.original, ['route'])}</>,
      },
      {
        Header: t('workTrips.period'),
        accessor: 'period',
        disableSortBy: true,
        Cell: ({ row }: CellProps<IReportRow>) => <>{formatPeriod(row.original)}</>,
      },
      {
        Header: t('workTrips.daysCount'),
        accessor: 'number_of_days',
        disableSortBy: true,
        Cell: ({ row }: CellProps<IReportRow>) => <>{getValue(row.original, ['number_of_days'])}</>,
      },
      {
        Header: t('workTrips.dailyAllowance'),
        accessor: 'daily_allowance',
        disableSortBy: true,
        Cell: ({ row }: CellProps<IReportRow>) => <>{getValue(row.original, ['daily_allowance'])}</>,
      },
      {
        Header: t('workTrips.totalCost'),
        accessor: 'total_amount',
        disableSortBy: true,
        Cell: ({ row }: CellProps<IReportRow>) => <>{getValue(row.original, ['total_amount'])}</>,
      },
      {
        Header: t('workTrips.status'),
        accessor: 'status',
        disableSortBy: true,
        Cell: ({ row }: CellProps<IReportRow>) => {
          const statusValue = getValue(row.original, ['status']);
          if (typeof statusValue === 'string' && statusValue.startsWith('workTrips.statuses.')) {
            return <>{t(statusValue)}</>;
          }
          if (typeof statusValue === 'string' && statusValue !== '-') {
            return <>{t(`workTrips.statuses.${statusValue}`)}</>;
          }
          return <>{statusValue}</>;
        },
      },
    ],
    [t]
  );

  return (
    <FetchTable<IReportRow>
      columns={columns}
      entityName="work-trips-report"
      entityEndpoint="reports/work-trips/json"
      requestType="POST"
      searchParams={reportParams}
      pageLimit={20}
      hideFilterBar
      hideSearch
      isReport
      pagination
    />
  );
};

export default TripsTable;
